import React, { FC, useState, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

import capitalizeFirstLetter from '../../../utils/capitalizeFirstLetter'

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    width: '100%',
    marginTop: theme.spacing(1),
    height: 45,
    '& .MuiInputBase-root': {
      paddingRight: 0,
      marginBottom: theme.spacing(0.5),
    },
    '&:hover .MuiInput-underline:before ': {
      borderBottom: `1px solid ${theme.palette.primary.main}`,
    },
    '& .MuiInput-underline:after ': {
      borderBottom: `1px solid ${theme.palette.info.light}`,
    },
    '& .MuiInputBase-input': {
      height: 20,
      fontSize: 14,
      fontWeight: 'normal',
      padding: '10px 16px',
      marginBottom: 0,
    },
    '& label.Mui-error': {
      color: '#3E3E3E',
    },
    '& .MuiButtonBase-root': {
      color: theme.palette.primary.light,
      opacity: 0.6,
    },
  },
}))

interface IFormTextFieldPassword {
  props?: TextFieldProps
  name: string
  placeholder: string
}

export const InputFieldPassword: FC<IFormTextFieldPassword> = ({ props, name, placeholder }) => {
  const classes = useStyles()
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const error = errors[name]

  const errorMessage = useMemo(() => {
    const message = error?.message.replace(name, '')
    return capitalizeFirstLetter(message)
  }, [error?.message, name])

  interface State {
    password: string
    showPassword: boolean
  }

  const [values, setValues] = useState<State>({
    password: '',
    showPassword: false,
  })

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword })
  }

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  return (
    <>
      <Controller
        control={control}
        name={name}
        rules={{ required: true }}
        defaultValue={''}
        render={({ field }) => (
          <TextField
            {...field}
            {...props}
            variant="outlined"
            error={!!error}
            type={values.showPassword ? 'text' : 'password'}
            placeholder={placeholder}
            className={classes.input}
            helperText={errorMessage}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {values.showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </>
  )
}
